import {createEntityMeta,SyncStatus} from '../offline/entity.js';
import {
  createSmokingStage,
  orderSmokingStages,
  removeSmokingStage,
  updateSmokingStage
} from './stage.js';

export const SMOKING_STAGE_ENTITY_TYPE='smoking_stage';
export const EDITABLE_STAGE_SESSION_STATUSES=Object.freeze(['draft','planned','preparation','ready']);
const EDITABLE=new Set(EDITABLE_STAGE_SESSION_STATUSES);

function required(value,label){
  const text=String(value||'').trim();
  if(!text)throw new TypeError(label+' is required');
  return text;
}

function assertEditable(status){
  const value=String(status||'').trim().toLowerCase();
  if(!EDITABLE.has(value))throw new Error('SMOKING_STAGES_SESSION_LOCKED');
  return value;
}

function conflict(row){
  return row?.syncStatus===SyncStatus.CONFLICT;
}

function repositoryRecord(row){
  const copy={...row};
  delete copy.key;
  delete copy.entityType;
  return copy;
}

function domainFromStored(row){
  return createSmokingStage(repositoryRecord(row));
}

function mergeStored(existing,domain,{now}){
  if(existing){
    return {
      ...repositoryRecord(existing),
      ...domain,
      id:existing.id,
      userId:existing.userId,
      sessionId:existing.sessionId
    };
  }
  return {
    ...createEntityMeta({id:domain.id,now,syncStatus:SyncStatus.PENDING_CREATE}),
    ...domain
  };
}

export function createSmokingStageStore({
  repository,
  queue,
  userId,
  now=()=>new Date().toISOString()
}={}){
  if(!repository?.get||!repository?.list||!repository?.put)throw new TypeError('repository with get/list/put is required');
  if(!queue?.enqueue||!queue?.markConflict)throw new TypeError('sync queue is required');
  const owner=required(userId,'userId');

  function assertOwner(row){
    if(row?.userId!==owner)throw new Error('SMOKING_STAGE_OWNER_MISMATCH');
    return row;
  }

  async function list(sessionId,{includeDeleted=false}={}){
    const session=required(sessionId,'sessionId');
    const rows=await repository.list(SMOKING_STAGE_ENTITY_TYPE);
    const owned=(rows||[])
      .filter(row=>row.userId===owner&&row.sessionId===session)
      .filter(row=>includeDeleted||!row.deletedAt);

    const ordered=orderSmokingStages(owned,{includeDeleted});
    const byId=new Map(owned.map(row=>[row.id,row]));
    return Object.freeze(ordered.map(stage=>Object.freeze({...byId.get(stage.id),...stage})));
  }

  async function get(id){
    const row=await repository.get(SMOKING_STAGE_ENTITY_TYPE,required(id,'id'));
    if(!row)return null;
    return Object.freeze(assertOwner(row));
  }

  async function create(sessionId,input={}, {sessionStatus='draft'}={}){
    assertEditable(sessionStatus);
    const session=required(sessionId,'sessionId');
    const existing=await list(session);
    const nextOrder=existing.length
      ?Math.max(...existing.map(stage=>Number(stage.sortOrder)||0))+1
      :0;
    const domain=createSmokingStage({
      ...input,
      userId:owner,
      sessionId:session,
      sortOrder:input.sortOrder??nextOrder
    });
    const entity=mergeStored(null,domain,{now:now()});
    return queue.enqueue(SMOKING_STAGE_ENTITY_TYPE,entity,'create');
  }

  async function createLiveCustom(sessionId,input={}, {sessionStatus='smoking'}={}){
    if(String(sessionStatus||'').trim().toLowerCase()!=='smoking'){
      throw new Error('LIVE_CUSTOM_STAGE_REQUIRES_SMOKING');
    }
    const session=required(sessionId,'sessionId');
    const existing=await list(session);
    const nextOrder=existing.length
      ?Math.max(...existing.map(stage=>Number(stage.sortOrder)||0))+1
      :0;
    const domain=createSmokingStage({
      id:input.id,
      userId:owner,
      sessionId:session,
      typeKey:'custom',
      nameOverride:required(input.nameOverride,'nameOverride'),
      sortOrder:nextOrder
    });
    const entity=mergeStored(null,domain,{now:now()});
    return queue.enqueue(SMOKING_STAGE_ENTITY_TYPE,entity,'create');
  }

  async function update(id,changes={}, {sessionStatus='draft'}={}){
    assertEditable(sessionStatus);
    const current=await get(id);
    if(!current)throw new Error('SMOKING_STAGE_NOT_FOUND');
    if(conflict(current))throw new Error('SMOKING_STAGE_CONFLICT');
    const domain=updateSmokingStage(domainFromStored(current),changes);
    const entity=mergeStored(current,domain,{now:now()});
    return queue.enqueue(SMOKING_STAGE_ENTITY_TYPE,entity,'update');
  }

  async function remove(id,{sessionStatus='draft',at=now()}={}){
    assertEditable(sessionStatus);
    const current=await get(id);
    if(!current)throw new Error('SMOKING_STAGE_NOT_FOUND');
    if(conflict(current))throw new Error('SMOKING_STAGE_CONFLICT');
    const domain=removeSmokingStage(domainFromStored(current),{at});
    const entity=mergeStored(current,domain,{now:at});
    return queue.enqueue(SMOKING_STAGE_ENTITY_TYPE,entity,'update');
  }

  async function reorder(sessionId,orderedIds,{sessionStatus='draft'}={}){
    assertEditable(sessionStatus);
    const current=await list(sessionId);
    const ids=Array.from(orderedIds||[],value=>required(value,'stage id'));
    const currentIds=current.map(stage=>stage.id);
    if(ids.length!==currentIds.length||new Set(ids).size!==ids.length){
      throw new TypeError('orderedIds must contain every active stage exactly once');
    }
    const allowed=new Set(currentIds);
    if(ids.some(id=>!allowed.has(id)))throw new TypeError('orderedIds contain an unknown stage');

    const updated=[];
    for(let index=0;index<ids.length;index+=1){
      const stage=current.find(row=>row.id===ids[index]);
      if(conflict(stage))throw new Error('SMOKING_STAGE_CONFLICT');
      if(stage.sortOrder===index){updated.push(stage);continue;}
      updated.push(await update(stage.id,{sortOrder:index},{sessionStatus}));
    }
    return Object.freeze(updated);
  }

  async function markConflict(id,details){
    const current=await get(id);
    if(!current)throw new Error('SMOKING_STAGE_NOT_FOUND');
    return queue.markConflict(SMOKING_STAGE_ENTITY_TYPE,current,details);
  }

  return Object.freeze({create,createLiveCustom,get,list,update,remove,reorder,markConflict});
}
